import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../styles/theme';
import { ErrorMessage } from '@common/Autocomplete';
import { Stack } from '@mui/material';
import {
  EditorState,
  EditorView,
  ViewPlugin,
  ViewUpdate,
  placeholder as insertPlaceholder,
} from '@uiw/react-codemirror';

interface Props {
  placeholder: string;
  value: string;
  keyProp?: string;
  minRows?: number;
  maxRows?: number;
  setValue: (value: string) => void;
  wrapping?: boolean;
  maxWidth?: string;
  disabled?: boolean;
  numberOnly?: boolean;
  minNumber?: number;
  maxNumber?: number;
  error?: string;
}

export const BasicInput: React.FC<Props> = ({
  keyProp,
  placeholder,
  value,
  setValue,
  minRows,
  maxRows,
  wrapping = true,
  maxWidth,
  disabled,
  numberOnly = false,
  minNumber,
  maxNumber,
  error,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const [editorView, setEditorView] = useState<EditorView | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!editorRef.current) return;

    // Define the minRows and maxRows constants
    const minInputRows = minRows || 1;
    const maxInputRows = maxRows || 12;
    const verticalPadding = 12;

    const calculateEditorHeight = (view: EditorView) => {
      const rowHeight = 28;
      const contentHeight = view.contentHeight + verticalPadding;
      const minHeight = rowHeight * minInputRows + verticalPadding;
      const maxHeight = rowHeight * maxInputRows + verticalPadding;

      return Math.max(minHeight, Math.min(maxHeight, contentHeight)) + 'px';
    };

    // Dynamic height plugin
    const dynamicHeightPlugin = ViewPlugin.fromClass(
      class {
        constructor(view: EditorView) {
          view.dom.style.height = calculateEditorHeight(view);
        }

        update(update: ViewUpdate) {
          update.view.dom.style.height = calculateEditorHeight(update.view);
        }
      }
    );

    const numberOnlyExtension = EditorState.transactionFilter.of((tr) => {
      if (!numberOnly || !tr.docChanged) return tr;

      const newDoc = tr.newDoc.toString();
      const isValidNumber = /^-?\d*\.?\d*$/.test(newDoc);

      if (!isValidNumber) return { changes: [], effects: tr.effects };
      return tr;
    });

    const extensions = [
      EditorView.theme({
        '&': {
          padding: '6px',
          paddingTop: '7px',
          paddingBottom: '3px',
          borderRadius: '6px',
          minHeight: '40px',
          color: disabled ? theme.text.disabled : theme.text.standard,
          cursor: disabled ? 'not-allowed' : 'text',
        },
        '.cm-scroller::-webkit-scrollbar': {
          display: !wrapping ? 'none' : '',
        },
        '.cm-content': {
          caretColor: disabled ? 'transparent' : theme.text.standard,
        },
        '.cm-scroller': {
          overflow: 'auto',
          fontFamily: 'Inter',
          fontSize: '14px',
          color: theme.text.standard,
          whiteSpace: !wrapping ? 'nowrap' : 'pre-wrap',
        },
        '&.cm-focused': {
          outline: disabled ? 'none' : `2px solid ${theme.primary} !important`,
          zIndex: 100,
          pointerEvents: disabled ? 'none' : 'auto',
        },
      }),
      dynamicHeightPlugin,
      EditorView.updateListener.of((update) => {
        if (!disabled && update.docChanged) {
          const docContent = update.state.doc.toString();
          setValue(docContent);

          if (numberOnly) {
            const numberValue = parseFloat(docContent);
            if (!isNaN(numberValue)) {
              if (minNumber !== undefined && numberValue < minNumber) {
                setErrorMessage(`Min value this number can be is ${minNumber}.`);
              } else if (maxNumber !== undefined && numberValue > maxNumber) {
                setErrorMessage(`Max value this number can be is ${maxNumber}.`);
              } else {
                setErrorMessage(null);
              }
            } else {
              setErrorMessage(null);
            }
          }
        }
      }),
      insertPlaceholder(placeholder),
      numberOnlyExtension,
    ];

    if (disabled) extensions.push(EditorView.editable.of(false));
    if (wrapping) extensions.push(EditorView.lineWrapping);

    const startState = EditorState.create({
      doc: value,
      extensions,
    });

    const view = new EditorView({
      state: startState,
      parent: editorRef.current,
    });

    setEditorView(view);

    return () => {
      view.destroy();
    };
  }, [theme, keyProp, disabled, numberOnly]);

  useEffect(() => {
    if (disabled) return;
    if (!editorView || value === editorView.state.doc.toString()) return;

    editorView.dispatch({
      changes: { from: 0, to: editorView.state.doc.length, insert: value },
    });
  }, [disabled, value, keyProp]);

  return (
    <Stack spacing={1}>
      <div
        ref={editorRef}
        style={{
          border: `1px solid ${theme.line.light}`,
          borderRadius: '6px',
          borderColor: errorMessage ? theme.telnyx.error : theme.line.light,
          minHeight: '40px',
          maxWidth: maxWidth || '',
          minWidth: maxWidth || '',
          pointerEvents: disabled ? 'none' : 'auto',
          opacity: disabled ? 0.5 : 1,
          color: disabled ? theme.text.disabled : theme.text.standard,
        }}
      />
      {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      {error && !errorMessage && <ErrorMessage>{error}</ErrorMessage>}
    </Stack>
  );
};
